import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import {
  createPortfolioItems,
  getPortfolioItemsByID,
  PortfolioItemsDetailsImageUpload,
  updatePortfolioItems,
} from "./services/portfolioItem.services";
import { clearPortfolioItemDetails } from "./portfolioItemSlice";
import { getAllActiveServices } from "../services/services/services.services";
import { slugify } from "../../_helper/helper";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreatePortfolioItems: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const PortfolioItemsID = location.state?.ID;
  const PortfolioItemsDetail = useAppSelector(
    (state) => state.portfolioitems.PortfolioItemDetail
  );
  const loading = useAppSelector((state) => state.portfolioitems.loading);
  const [formKey, setFormKey] = React.useState(1);
  const ServiceList = useAppSelector((state) => state.services.ServiceList);

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  useEffect(() => {
    if (PortfolioItemsID) {
      dispatch(getPortfolioItemsByID(PortfolioItemsID));
    }
    dispatch(getAllActiveServices());
    return () => {
      dispatch(clearPortfolioItemDetails());
    };
  }, [PortfolioItemsID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PortfolioItemsDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      ServiceID: values.ServiceID ?? null,
      Title: values.Title ?? "",
      Description: values.Description ?? "",
      PageContent: values.PageContent ?? "",
      URL: values.URL ?? "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const CardImage =
      values?.CardImage &&
      values?.CardImage.length &&
      typeof values?.CardImage !== "string"
        ? values.CardImage[0].getRawFile()
        : null;
    const PortfolioBannerImage =
      values?.PortfolioBannerImage &&
      values?.PortfolioBannerImage.length &&
      typeof values?.PortfolioBannerImage !== "string"
        ? values.PortfolioBannerImage[0].getRawFile()
        : null;
    try {
      if (PortfolioItemsID) {
        const editPayload = {
          ...formData,
          ID: PortfolioItemsID,
        };

        const response = await dispatch(updatePortfolioItems(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.CardImage && typeof values?.CardImage !== "string") ||
            (values?.PortfolioBannerImage &&
              typeof values?.PortfolioBannerImage !== "string")
          ) {
            ImageData.append("CardImage", CardImage);
            ImageData.append("PortfolioBannerImage", PortfolioBannerImage);
            ImageData.append("ID", PortfolioItemsID);
            const imageResponse = await dispatch(
              PortfolioItemsDetailsImageUpload(ImageData)
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/portfolioitems");
            } else {
              navigate("/portfolioitems");
            }
          } else {
            navigate("/portfolioitems");
          }
        }
      } else {
        const response = await dispatch(createPortfolioItems(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.CardImage && typeof values?.CardImage !== "string") ||
            (values?.PortfolioBannerImage &&
              typeof values?.PortfolioBannerImage !== "string" &&
              response?.payload?.Data?.ID)
          ) {
            ImageData.append("CardImage", CardImage);
            ImageData.append("PortfolioBannerImage", PortfolioBannerImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              PortfolioItemsDetailsImageUpload(ImageData)
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/portfolioitems");
            } else {
              navigate("/portfolioitems");
            }
          } else {
            navigate("/portfolioitems");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      <div>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={PortfolioItemsDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {PortfolioItemsID
                        ? "Update  Portfolio Item"
                        : "Create  Portfolio Item"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="URL"
                      label="URL"
                      placeholder="URL"
                      astrike={true}
                      component={FormTextField}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`ServiceID`}
                      label="Service ID"
                      placeholder="Service ID"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={ServiceList?.map((service: any) => {
                        return {
                          value: service?.ID,
                          label: service?.Title,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Description"
                      label="Description"
                      placeholder="Description"
                      component={FormTextArea}
                      minHeight={50}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="PageContent"
                      label="Portfolio Content"
                      placeholder="Portfolio Content"
                      component={FormRichTextField}
                      minHeight={500}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`CardImage`}
                      label="Card Image"
                      type="file"
                      accept="image/*"
                      alternateName="Card Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`PortfolioBannerImage`}
                      label="Portfolio Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Portfolio Banner Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={PortfolioItemsID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/portfolioitems")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreatePortfolioItems;
